import { VoiceCrossFrameWrapper } from '@services/voice/VoiceCrossFrameWrapper';

const voiceCrossFrameWrapper = new VoiceCrossFrameWrapper();

/**
 * Client for the peppermintService in base tool. {@see peppermintService} in base tool
 * for more documentation on how this client can be used and how it can fail.
 */
export class PeppermintClient {
    /**
     * Warms up the peppermint client so that the first call to peppermint
     * doesn't get impacted by this latency.
     *
     * The caller of this public static is encouraged to catch any exceptions and ignore them since it
     * could be a transient issue and a call to peppermint might still work if the transient
     * issue has been resolved (with some added latency). This is unless you get an
     * UnsupportedBrowserException.
     *
     * @return {Promise} Resolves when warmup finishes successfully and rejects when it doesn't.
     */
    public static warmup() {
        return PeppermintClient.makeCrossFrameCall('warmup');
    }

    /**
     * Calls peppermintService getAlphanumeric with the specified config.
     * @param {object} config - optional argument to configure the call.
     * @return {Promise} Resolves with a {ResponseObject} (see docs in base tool).
     */
    public static getAlphanumeric(config: any) {
        return PeppermintClient.makeCrossFrameCall('getAlphanumeric', config);
    }

    /**
     * Calls peppermintService.getConfirmation with the specified config.
     * @param {object} config - optional argument to configure the call.
     * @return {Promise} Resolves with a {ResponseObject} (see docs in base tool).
     */
    public static getConfirmation(config: any) {
        return PeppermintClient.makeCrossFrameCall('getConfirmation', config);
    }

    /**
     * Calls peppermintService.getCustomDataType with the specified config.
     * @param {object} config - required and must have the 'dataType' key.
     * @return {Promise} Resolves with a {ResponseObject} (see docs in base tool).
     */
    public static getCustomDataType(config: any) {
        return PeppermintClient.makeCrossFrameCall('getCustomDataType', config);
    }

    /**
     * Calls peppermintService.getDate with the specified config.
     * @param {object} config - optional argument to configure the call.
     * @return {Promise} Resolves with a {ResponseObject} (see docs in base tool).
     */
    public static getDate(config: any) {
        return PeppermintClient.makeCrossFrameCall('getDate', config);
    }

    /**
     * Calls peppermintService.getNumber with the specified config.
     * @param {object} config - optional argument to configure the call.
     * @return {Promise} Resolves with a {ResponseObject} (see docs in base tool).
     */
    public static getNumber(config: any) {
        return PeppermintClient.makeCrossFrameCall('getNumber', config);
    }

    /**
     * Calls peppermintService.getPositiveInteger with the specified config.
     * @param {object} config - optional argument to configure the call.
     * @return {Promise} Resolves with a {ResponseObject} (see docs in base tool).
     */
    public static getPositiveInteger(config: any) {
        return PeppermintClient.makeCrossFrameCall('getPositiveInteger', config);
    }

    /**
     * Makes the cross frame call to base tool.
     */
    private static makeCrossFrameCall(action: string, parameters?: any) {
        return voiceCrossFrameWrapper.call('peppermintListener', action, parameters);
    }
}
